import type { ID } from "@/lib/catalog/types";
import { api } from "@/lib/apiClient";
import { isAxiosError } from "axios";
import { isValid, parseISO } from "date-fns";

export interface WorkShiftRecord {
  id: string;
  name: string;
  /** Derived on the server from the shift name */
  slug: string;
  /** Local time HH:mm */
  startTime: string;
  endTime: string;
  breakMinutes: number;
  crossesMidnight: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export type WorkShiftDraft = {
  name: string;
  startTime: string;
  endTime: string;
  breakMinutes: number;
  crossesMidnight: boolean;
  active: boolean;
};

const LS_KEY = "inventory.workShifts.v1";

type WorkShiftApiRecord = {
  id?: number | string;
  name?: string | null;
  slug?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  break_minutes?: number | string | null;
  crosses_midnight?: boolean | null;
  active?: boolean | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type UpsertApiResponse = { message?: string; data?: WorkShiftApiRecord };
type DeleteApiResponse = { message?: string };

function dedupeById(items: WorkShiftRecord[]): WorkShiftRecord[] {
  const byId = new Map<ID, WorkShiftRecord>();
  for (const item of items) {
    byId.set(item.id, item);
  }
  return Array.from(byId.values());
}

function str(v: unknown): string {
  if (v == null || v === "") return "";
  return String(v);
}

/** Normalize API `start_time` / `end_time` to `HH:mm` (never use slice on datetime strings). */
function normalizeApiTimeToHHmm(raw: unknown): string {
  if (raw == null || raw === "") return "";
  const s = String(raw).trim();
  const direct = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(s);
  if (direct) {
    let h = parseInt(direct[1], 10);
    let m = parseInt(direct[2], 10);
    if (Number.isNaN(h) || h < 0 || h > 23) return "";
    if (Number.isNaN(m) || m < 0 || m > 59) return "";
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  }
  const isoLike = /\d{4}-\d{2}-\d{2}\s/.test(s) ? s.replace(/^(\d{4}-\d{2}-\d{2})\s/, "$1T") : s;
  const d = parseISO(isoLike);
  if (isValid(d)) {
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  }
  const ts = Date.parse(s);
  if (!Number.isNaN(ts)) {
    const x = new Date(ts);
    return `${String(x.getHours()).padStart(2, "0")}:${String(x.getMinutes()).padStart(2, "0")}`;
  }
  return "";
}

function extractRowsFromResponse(body: unknown): WorkShiftApiRecord[] {
  if (Array.isArray(body)) return body as WorkShiftApiRecord[];
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;
  const fromBlock = (block: unknown): WorkShiftApiRecord[] | null => {
    if (Array.isArray(block)) return block as WorkShiftApiRecord[];
    if (block && typeof block === "object" && !Array.isArray(block)) {
      const inner = (block as Record<string, unknown>).data;
      if (Array.isArray(inner)) return inner as WorkShiftApiRecord[];
    }
    return null;
  };
  for (const key of ["data", "work_shifts", "items", "results"]) {
    const hit = fromBlock(root[key]);
    if (hit) return hit;
  }
  return [];
}

function readLastPageFromListBody(body: unknown): number | null {
  if (!body || typeof body !== "object") return null;
  const meta = (body as Record<string, unknown>).meta;
  if (meta && typeof meta === "object" && !Array.isArray(meta)) {
    const lp = (meta as Record<string, unknown>).last_page;
    if (typeof lp === "number" && lp >= 1) return lp;
  }
  return null;
}

function extractFromUpsertBody(body: unknown): WorkShiftApiRecord | null {
  if (!body || typeof body !== "object") return null;
  const root = body as Record<string, unknown>;
  const data = root.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const block = data as Record<string, unknown>;
    if ("id" in block || typeof block.name === "string") {
      return block as unknown as WorkShiftApiRecord;
    }
  }
  if ("id" in root && typeof root.name === "string") {
    return root as unknown as WorkShiftApiRecord;
  }
  return null;
}

function mapApiRecord(record: WorkShiftApiRecord): WorkShiftRecord {
  const created = record.created_at || new Date().toISOString();
  const updated = record.updated_at || created;
  const br = record.break_minutes;
  const breakMinutes = br == null || br === "" ? 0 : Math.max(0, Number(br)) || 0;
  return {
    id: String(record.id ?? ""),
    name: str(record.name),
    slug: str(record.slug),
    startTime: normalizeApiTimeToHHmm(record.start_time) || "09:00",
    endTime: normalizeApiTimeToHHmm(record.end_time) || "17:00",
    breakMinutes,
    crossesMidnight: Boolean(record.crosses_midnight),
    active: record.active !== false,
    createdAt: created,
    updatedAt: updated,
  };
}

function buildApiPayload(draft: WorkShiftDraft) {
  return {
    name: draft.name.trim(),
    start_time: draft.startTime.trim(),
    end_time: draft.endTime.trim(),
    break_minutes: draft.breakMinutes,
    crosses_midnight: draft.crossesMidnight,
    active: draft.active,
  };
}

function load(): WorkShiftRecord[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as WorkShiftRecord[];
    return Array.isArray(parsed) ? dedupeById(parsed) : [];
  } catch {
    return [];
  }
}

function save(items: WorkShiftRecord[]) {
  localStorage.setItem(LS_KEY, JSON.stringify(dedupeById(items)));
}

const PAGE_SIZE = 20;

export function workShiftApiErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors).flat()[0];
      if (first) return first;
    }
    if (typeof data?.message === "string") return data.message;
  }
  return "Something went wrong. Please try again.";
}

export function listWorkShifts(): WorkShiftRecord[] {
  return load().sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function getWorkShiftById(id: ID): WorkShiftRecord | undefined {
  return load().find((s) => s.id === id);
}

export async function syncWorkShiftsFromApi(): Promise<WorkShiftRecord[]> {
  try {
    const collected: WorkShiftApiRecord[] = [];
    let page = 1;
    let lastPage = 1;
    do {
      const response = await api.get("/api/v1/work-shifts", {
        params: { page, per_page: PAGE_SIZE },
      });
      collected.push(...extractRowsFromResponse(response.data));
      lastPage = readLastPageFromListBody(response.data) ?? 1;
      page += 1;
    } while (page <= lastPage && page <= 50);

    const records = dedupeById(collected.map(mapApiRecord).filter((r) => Boolean(r.id)));
    save(records);
    return listWorkShifts();
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 403) {
      throw error;
    }
    console.error("[WorkShifts] sync failed", error);
    return listWorkShifts();
  }
}

export async function createWorkShift(draft: WorkShiftDraft): Promise<{ record: WorkShiftRecord; message: string }> {
  const items = load();
  const response = await api.post<UpsertApiResponse>("/api/v1/work-shifts", buildApiPayload(draft));
  const created = extractFromUpsertBody(response.data);
  if (!created || created.id == null) {
    throw new Error("Invalid create work shift response.");
  }
  const record = mapApiRecord(created);
  const idx = items.findIndex((s) => s.id === record.id);
  if (idx >= 0) items[idx] = record;
  else items.unshift(record);
  save(items);
  return { record, message: response.data?.message || "Work shift created successfully." };
}

export async function updateWorkShift(
  id: ID,
  draft: WorkShiftDraft,
): Promise<{ record: WorkShiftRecord; message: string } | null> {
  const items = load();
  const idx = items.findIndex((s) => s.id === id);
  if (idx === -1) return null;
  const response = await api.put<UpsertApiResponse>(`/api/v1/work-shifts/${id}`, buildApiPayload(draft));
  const updated = extractFromUpsertBody(response.data);
  if (!updated) {
    throw new Error("Invalid update work shift response.");
  }
  const record = mapApiRecord(updated);
  items[idx] = record;
  save(items);
  return { record, message: response.data?.message || "Work shift updated successfully." };
}

export async function deleteWorkShift(id: ID): Promise<{ success: boolean; message: string }> {
  const response = await api.delete<DeleteApiResponse>(`/api/v1/work-shifts/${id}`);
  const items = load();
  const next = items.filter((s) => s.id !== id);
  const removed = next.length !== items.length;
  if (removed) save(next);
  return {
    success: removed,
    message: response.data?.message || "Work shift deleted successfully.",
  };
}
